import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import {
  selectDonorInfo,
  selectIsCompanyGift,
  selectDonationInfo,
  selectCallCentreNumber,
  selectSource,
  selectSecuritiesInfo,
} from "redux/form/form.selector";
import {
  selectCurrentForm,
  selectIsFormDisabled,
  selectPopup,
  selectPresValue,
} from "redux/helpers/helpers.selector";
import { updateFormErrors } from "redux/form/form.reducer";
import { updateCurrentForm, updatePopup } from "redux/helpers/helpers.reducer";
import { scrollToTheError, validate } from "utils/helper-functions";
import { FormSourceType, ValidationErrors } from "utils/interfaces";
import {
  gosBrokerManagedInstitutionSchema,
  gosDonorInfoSchema,
  gosSelfManagedInstitutionSchema,
  schemaConstructor,
} from "utils/validation-schemas";
import analyticsAPI from "utils/analyticsAPI";
import { popupMessages } from "utils/form-constants";
import { CountryCode } from "libphonenumber-js";
import lookup from "country-code-lookup";
import { ValidationError } from "yup";

export const useNavigationHandler = (isGosForm?: boolean) => {
  const currentForm = useAppSelector(selectCurrentForm);
  const donorInfo = useAppSelector(selectDonorInfo);
  const isCompanyGift = useAppSelector(selectIsCompanyGift);
  const donationInfo = useAppSelector(selectDonationInfo);
  const securitiesInfo = useAppSelector(selectSecuritiesInfo);
  const callCentreNumber = useAppSelector(selectCallCentreNumber);
  const presValue = useAppSelector(selectPresValue);
  const source = useAppSelector(selectSource);
  const popup = useAppSelector(selectPopup);
  const isFormDisabled = useAppSelector(selectIsFormDisabled);
  const dispatch = useAppDispatch();
  const appInsights = useAppInsightsContext();

  const isCCForm = presValue === "CC";
  const isDevelopmentForm = source === FormSourceType.DEVELOPMENT_TEAM;
  const isGPForm = source === FormSourceType.GP_TEAM;
  const isKckForm = source === FormSourceType.KCK_FORM;
  const isFDForm = source === FormSourceType.FUNDRAISING_DIRECT;
  const isMemorialGivingForm = source === FormSourceType.MEMORIAL_GIVING_FORM;
  const isSelfManaged =
    securitiesInfo.securitiesManagementType === "self-directed";

  const handleGosNextForm = async (
    e: React.MouseEvent<HTMLButtonElement>,
    buttonID: number,
    buttonText: string,
  ) => {
    e.preventDefault();

    analyticsAPI.handleNavigationButton(appInsights, { buttonID, buttonText });
    if (buttonID === currentForm) {
      return;
    } else if (buttonID < currentForm) {
      dispatch(updateCurrentForm(buttonID));
      return;
    }

    if (currentForm === 0) {
      try {
        await validate(
          {
            ...donorInfo,
            securitiesManagementType: securitiesInfo.securitiesManagementType,
          },
          gosDonorInfoSchema,
        );

        if (buttonID === 1) {
          dispatch(updateCurrentForm(1));
          dispatch(updateFormErrors({}));
        } else if (buttonID === 2) {
          try {
            await validate(
              {
                ...securitiesInfo,
              },
              isSelfManaged
                ? gosSelfManagedInstitutionSchema
                : gosBrokerManagedInstitutionSchema,
            );
            dispatch(updateCurrentForm(2));
            dispatch(updateFormErrors({}));
          } catch (error: unknown) {
            const errors: ValidationErrors = {};

            (error as ValidationError).inner.forEach((err) => {
              if (err.path) {
                errors[err.path] = err.message;
              }
            });

            dispatch(updateCurrentForm(1));
            dispatch(updateFormErrors(errors));
            dispatch(
              updatePopup({
                ...popup,
                isActive: true,
                message: popupMessages.errorsFixed,
                isError: true,
                isLoading: false,
              }),
            );
          }
        }
      } catch (error: unknown) {
        const errors: ValidationErrors = {};

        (error as ValidationError).inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = err.message;
          }
        });

        scrollToTheError(Object.keys(errors)[0]);

        dispatch(updateFormErrors(errors));
        dispatch(
          updatePopup({
            ...popup,
            isActive: true,
            message: popupMessages.errorsFixed,
            isError: true,
            isLoading: false,
          }),
        );
      }
    } else if (currentForm === 1) {
      try {
        await validate(
          {
            ...securitiesInfo,
          },
          isSelfManaged
            ? gosSelfManagedInstitutionSchema
            : gosBrokerManagedInstitutionSchema,
        );
        dispatch(updateCurrentForm(2));
        dispatch(updateFormErrors({}));
      } catch (error: unknown) {
        const errors: ValidationErrors = {};

        (error as ValidationError).inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = err.message;
          }
        });

        scrollToTheError(Object.keys(errors)[0]);

        dispatch(updateFormErrors(errors));
        dispatch(
          updatePopup({
            ...popup,
            isActive: true,
            message: popupMessages.errorsFixed,
            isError: true,
            isLoading: false,
          }),
        );
      }
    }
  };

  const handleDefaultNextForm = async (
    e: React.MouseEvent<HTMLButtonElement>,
    buttonID: number,
    buttonText: string,
  ) => {
    e.preventDefault();

    analyticsAPI.handleNavigationButton(appInsights, { buttonID, buttonText });
    if (buttonID === currentForm) {
      return;
    } else if (buttonID < currentForm) {
      dispatch(updateCurrentForm(buttonID));
      return;
    }

    const donationSchema = schemaConstructor("donationType", {
      isInternalForm: isCCForm || isFDForm || isDevelopmentForm,
      isGPForm,
      isDevelopmentForm,
      isMemorialGivingForm,
    });
    const donorSchema = schemaConstructor("donorInfo", {
      locale: lookup.byCountry(donorInfo.country)?.iso2 as CountryCode,
      presValue,
      isInternalForm: isFDForm || isCCForm || isDevelopmentForm,
      isGPForm,
      isDevelopmentForm,
      isKckForm,
    });

    if (currentForm === 0) {
      try {
        await validate(
          {
            donationAmount: donationInfo.donationAmount,
            donationType: donationInfo.donationType,
            honourType: donationInfo.honourType,
            honoureeName: donationInfo.honoureeName,
            honoureeFirstName: donationInfo.honoureeFirstName,
            honoureeLastName: donationInfo.honoureeLastName,
            recipientFirstName: donationInfo.recipientFirstName,
            recipientLastName: donationInfo.recipientLastName,
            recipientEmail: donationInfo.recipientEmail,
            emailSubject: donationInfo.emailSubject,
            cardActive: donationInfo.cardActive,
            honoureeAddressLine: donationInfo.honoureeAddressLine,
            honoureeCity: donationInfo.honoureeCity,
            honoureeProvinceState: donationInfo.honoureeProvinceState,
            honoureeCountry: donationInfo.honoureeCountry,
            honoureePostal: donationInfo.honoureePostal,
            cardRecipientFullName: donationInfo.cardRecipientFullName,
            isTributeGift: donationInfo.isTributeGift,
            honourCardType: donationInfo.honourCardType,
            loyaltyCardNumber: donationInfo.loyaltyCardNumber,
            loyaltyCardNumberRepeat: donationInfo.loyaltyCardNumberRepeat,
          },
          donationSchema,
        );

        if (buttonID === 1) {
          dispatch(updateCurrentForm(1));
          dispatch(updateFormErrors({}));
        } else if (buttonID === 2) {
          try {
            await validate(
              {
                ...donorInfo,
                isCompanyGift,
                callCentreNumber,
                callCentreAgentName: donationInfo.callCentreAgentName,
                appealPackage: donationInfo.appealPackage,
                appealCodeOverride: donationInfo.appealCodeOverride,
              },
              donorSchema,
            );
            dispatch(updateCurrentForm(2));
            dispatch(updateFormErrors({}));
          } catch (error: unknown) {
            const errors: ValidationErrors = {};

            (error as ValidationError).inner.forEach((err) => {
              if (err.path) {
                errors[err.path] = err.message;
              }
            });

            dispatch(updateCurrentForm(1));
            dispatch(updateFormErrors(errors));
            dispatch(
              updatePopup({
                ...popup,
                isActive: true,
                message: popupMessages.errorsFixed,
                isError: true,
                isLoading: false,
              }),
            );
          }
        }
      } catch (error: unknown) {
        const errors: ValidationErrors = {};

        (error as ValidationError).inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = err.message;
          }
        });

        scrollToTheError(Object.keys(errors)[0]);

        dispatch(updateFormErrors(errors));
        dispatch(
          updatePopup({
            ...popup,
            isActive: true,
            message: popupMessages.errorsFixed,
            isError: true,
            isLoading: false,
          }),
        );
      }
    } else if (currentForm === 1) {
      try {
        await validate(
          {
            ...donorInfo,
            isCompanyGift,
            callCentreNumber,
            callCentreAgentName: donationInfo.callCentreAgentName,
            appealPackage: donationInfo.appealPackage,
            appealCodeOverride: donationInfo.appealCodeOverride,
          },
          donorSchema,
        );
        dispatch(updateCurrentForm(2));
        dispatch(updateFormErrors({}));
      } catch (error: unknown) {
        const errors: ValidationErrors = {};

        (error as ValidationError).inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = err.message;
          }
        });

        scrollToTheError(Object.keys(errors)[0]);

        dispatch(updateFormErrors(errors));
        dispatch(
          updatePopup({
            ...popup,
            isActive: true,
            message: popupMessages.errorsFixed,
            isError: true,
            isLoading: false,
          }),
        );
      }
    }
  };

  const handleNextForm = isGosForm ? handleGosNextForm : handleDefaultNextForm;

  return { handleNextForm, currentForm, isFormDisabled };
};
